/**
 * User-Facing Messages Constants
 * 
 * Centralized user-facing strings for consistency and future i18n support
 */

// Progress Tracker Messages
export const PROGRESS_MESSAGES = {
  PROGRESS_TRACKER: (subject) => `${subject} Progress Tracker`,
  PROGRESS: (subject) => `${subject} Progress`,
  ACCURACY: 'Accuracy',
  STREAK: 'Streak',
  TOTAL: 'Total',
  QUESTIONS_TRIED: 'Questions Tried',
  AVERAGE_TIME: 'Avg. Time',
  MASTERY_PROGRESS: 'Mastery Progress',
  NEXT_MILESTONE: (milestone) => `Next milestone: ${milestone}`,
  STRENGTHS: '💪 Strengths',
  FOCUS_AREAS: '🎯 Focus Areas',
  KEEP_PRACTICING: 'Keep practicing to discover your strengths!',
  GREAT_JOB: 'Great job! No specific areas need focus right now.',
  RECOMMENDED_NEXT_STEPS: '🎓 Recommended Next Steps',
  CONSISTENCY_SCORE: 'Consistency Score',
  CONSISTENCY_DESCRIPTION: 'How steady your performance has been recently',
  CURRENT_ACCURACY: (accuracy) => `Current accuracy: ${accuracy}%`,
  ESTIMATED_TIME: (time) => `Est. time: ${time} min`
};

// Achievement Messages
export const ACHIEVEMENT_MESSAGES = {
  UNLOCKED: '🎉 Achievement Unlocked! 🎉',
  ACHIEVEMENT: (category) => `${category} Achievement`,
  POINTS: (points) => `${points} pts`
};

// Subject Names (Display)
export const SUBJECT_DISPLAY_NAMES = {
  phonics: 'Phonics',
  math: 'Math',
  reading: 'Reading',
  spelling: 'Spelling',
  science: 'Science',
  social: 'Social Studies',
  skipcounting: 'Skip Counting',
  art: 'Art',
  geography: 'Geography',
  history: 'History'
};

// Priority Labels
export const PRIORITY_LABELS = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low'
};

// Mastery Levels
export const MASTERY_LEVELS = {
  EXPERT: 'expert',
  PROFICIENT: 'proficient',
  DEVELOPING: 'developing',
  BEGINNER: 'beginner'
};

// Trend Icons
export const TREND_ICONS = {
  IMPROVING: '📈',
  DECLINING: '📉',
  STABLE: '➡️'
};

// Streak Status Icons
export const STREAK_ICONS = {
  FIRE: '🔥',
  HOT: '⭐',
  WARM: '🌟',
  NORMAL: '💫'
};

// Loading States
export const LOADING_MESSAGES = {
  LOADING: 'Loading...',
  LOADING_QUESTIONS: 'Loading questions...',
  LOADING_PROGRESS: 'Loading progress...',
  INITIALIZING: 'Initializing...'
};

// Error Messages (User-Friendly)
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Unable to connect. Please check your internet and try again.',
  LOADING_ERROR: 'Failed to load content. Please try again.',
  UNKNOWN_ERROR: 'Something went wrong. Please try again.',
  RETRY: 'Retry',
  GO_BACK: 'Go Back',
  GAME_START_FAILED: 'Unable to start game. Please try again.',
  FEEDBACK_SUBMIT_FAILED: 'Failed to submit feedback. Please try again.',
  ERROR_REPORT_FAILED: 'Failed to report error. Please try again.',
  ERROR_REPORT_SUCCESS: 'Error reported successfully! Thank you for helping us improve.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  PERMISSION_ERROR: 'Permission denied. Please check your settings.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
  ERROR_DETAILS_COPIED: 'Error details copied to clipboard. Please share with support if needed.',
  ERROR_REPORT_REFRESH: 'Failed to report error. Please try refreshing the page.',
  SOMETHING_WENT_WRONG: 'Something went wrong',
  OOPS: 'Oops!',
  TRY_AGAIN: 'Try Again',
  RELOAD_PAGE: 'Reload Page',
  START_FRESH: 'Start Fresh',
  REPORTING: 'Reporting...',
  REPORT_ERROR: 'Report Error',
  HIDE_ERROR_DETAILS: 'Hide',
  SHOW_ERROR_DETAILS: 'Show Error Details',
  ERROR_ID: 'Error ID:',
  ERROR_TYPE: 'Type:',
  ERROR_MESSAGE: 'Message:',
  ERROR_URL: 'URL:',
  ERROR_TIME: 'Time:',
  ERROR_RETRIES: 'Retries:',
  RETRY_ATTEMPT: (count, max) => `We've tried ${count} time${count > 1 ? 's' : ''} to fix this.`,
  DONT_WORRY: "Don't worry, Emmy's Learning App is still here!",
  TRY_AGAIN_WITH_COUNT: (count, max) => `🔄 Try Again ${count > 0 ? `(${count}/${max})` : ''}`
};

// Success Messages
export const SUCCESS_MESSAGES = {
  SAVED: 'Saved successfully!',
  UPDATED: 'Updated successfully!',
  COMPLETED: 'Completed!'
};

// Game/Question Messages
export const GAME_MESSAGES = {
  QUESTION: (current, total) => `Question ${current} of ${total}`,
  SCORE: (score) => `Score: ${score}`,
  CORRECT: 'Correct!',
  NOT_QUITE_RIGHT: 'Not quite right!',
  TRY_AGAIN: 'Try Again'
};

// Subject-Specific Loading Messages
export const SUBJECT_LOADING_MESSAGES = {
  GEOGRAPHY: 'Loading Geography Explorer...',
  HISTORY: 'Loading History Adventure...',
  SCIENCE: 'Loading Science Adventure...',
  ART: 'Loading Art Studio...',
  MATH: 'Loading Math Adventure...',
  PHONICS: 'Loading Phonics Adventure...',
  READING: 'Loading Reading Adventure...',
  SPELLING: 'Loading Spelling Adventure...'
};

// Subject Titles
export const SUBJECT_TITLES = {
  GEOGRAPHY: 'Geography Explorer',
  HISTORY: 'History Adventure',
  SCIENCE: 'Science Adventure',
  ART: 'Art Studio',
  MATH: 'Math Adventure',
  PHONICS: 'Phonics Adventure',
  READING: 'Reading Adventure',
  SPELLING: 'Spelling Adventure'
};

// Subject Error Messages
export const SUBJECT_ERROR_MESSAGES = {
  GEOGRAPHY: 'Failed to load Geography content',
  HISTORY: 'Failed to load History content',
  SCIENCE: 'Failed to load Science content',
  ART: 'Failed to load Art content',
  MATH: 'Failed to load Math content',
  PHONICS: 'Failed to load Phonics content',
  READING: 'Failed to load Reading content',
  SPELLING: 'Failed to load Spelling content'
};

// Notification Messages
export const NOTIFICATION_MESSAGES = {
  TITLE: 'Learning Reminders',
  NOT_SUPPORTED_TITLE: 'Notifications Not Supported',
  NOT_SUPPORTED_DESCRIPTION: 'Your browser doesn\'t support push notifications. You can still use all learning features!',
  ENABLE_TITLE: 'Enable Learning Reminders',
  ENABLE_DESCRIPTION: 'Get friendly reminders to keep your learning streak going! We\'ll send you:',
  ENABLE_BUTTON: 'Enable Reminders',
  ENABLING: 'Enabling...',
  REMINDERS_ENABLED: 'Reminders Enabled',
  REMINDERS_ENABLED_DESCRIPTION: 'You\'ll receive learning notifications',
  DISABLE: 'Disable',
  REMINDER_SETTINGS: 'Reminder Settings',
  DAILY_REMINDER: 'Daily Learning Reminder',
  DAILY_REMINDER_DESC: 'Get reminded to practice every day',
  REMINDER_TIME: 'Reminder Time',
  WEEKLY_GOAL: 'Weekly Progress Check',
  WEEKLY_GOAL_DESC: 'Review your weekly learning progress',
  STREAK_REMINDERS: 'Streak Reminders',
  STREAK_REMINDERS_DESC: 'Don\'t break your learning streak!',
  ACHIEVEMENT_CELEBRATIONS: 'Achievement Celebrations',
  ACHIEVEMENT_CELEBRATIONS_DESC: 'Celebrate when you earn new badges',
  SETTINGS_FOOTER: 'You can change these settings anytime. Notifications help you stay consistent with learning!',
  DAILY_REMINDER_LABEL: 'Daily learning reminders',
  WEEKLY_CHECK_IN_LABEL: 'Weekly progress check-ins',
  STREAK_ALERTS_LABEL: 'Streak maintenance alerts',
  ACHIEVEMENT_CELEBRATIONS_LABEL: 'Achievement celebrations'
};

// PWA Install Messages
export const PWA_MESSAGES = {
  INSTALL_TITLE: 'Install Emmy\'s Learning App',
  INSTALL_DESCRIPTION: 'Get the full app experience! Install Emmy\'s Learning Adventure for:',
  INSTALL_APP: 'Install App',
  NOT_NOW: 'Not Now',
  WORKS_OFFLINE: 'Works offline',
  FASTER_LOADING: 'Faster loading',
  HOME_SCREEN_ACCESS: 'Home screen access',
  LEARNING_REMINDERS: 'Learning reminders',
  ONBOARDING_WELCOME_TITLE: 'Welcome to Emmy\'s Learning App! 🎉',
  ONBOARDING_WELCOME_CONTENT: 'You\'ve successfully installed the app! Now you can learn anytime, anywhere - even without internet.',
  ONBOARDING_OFFLINE_TITLE: 'Learn Offline 📱',
  ONBOARDING_OFFLINE_CONTENT: 'All your favorite subjects and questions are now available offline. Perfect for car rides or anywhere without WiFi!',
  ONBOARDING_PROGRESS_TITLE: 'Track Your Progress 📊',
  ONBOARDING_PROGRESS_CONTENT: 'Your learning progress is automatically saved and synced when you\'re back online. Never lose your achievements!',
  ONBOARDING_REMINDERS_TITLE: 'Get Learning Reminders 🔔',
  ONBOARDING_REMINDERS_CONTENT: 'Want daily learning reminders? We can send you friendly notifications to keep your learning streak going!',
  SKIP: 'Skip',
  NEXT: 'Next',
  START_LEARNING: 'Start Learning!'
};

// Audio Preferences Messages
export const AUDIO_MESSAGES = {
  TITLE: 'Audio Settings',
  AUDIO_STATUS: 'Audio Status',
  AUDIO_SUPPORT: 'Audio Support:',
  INITIALIZED: 'Initialized:',
  HAPTIC_SUPPORT: 'Haptic Support:',
  SUPPORTED: '✓ Supported',
  NOT_SUPPORTED: '✗ Not Supported',
  READY: '✓ Ready',
  PENDING: '⏳ Pending',
  AVAILABLE: '✓ Available',
  NOT_AVAILABLE: '✗ Not Available',
  VOLUME: 'Volume',
  SOUND_EFFECTS: 'Sound Effects',
  SOUND_EFFECTS_DESC: 'Enable audio feedback for interactions',
  HAPTIC_FEEDBACK: 'Haptic Feedback',
  HAPTIC_FEEDBACK_DESC: 'Vibration feedback on mobile devices',
  TEST_SOUNDS: 'Test Sounds',
  CORRECT: '✓ Correct',
  INCORRECT: '✗ Incorrect',
  COMPLETE: '🎉 Complete',
  ACHIEVEMENT: '🏆 Achievement',
  ENABLE_AUDIO: '🎵 Enable Audio'
};

// App-Level Messages
export const APP_MESSAGES = {
  // Loading States
  LOADING_APP: 'Loading Emmy\'s Learning App...',
  INITIALIZING_APP: 'Initializing Emmy\'s Learning App...',
  DEBUG_INITIALIZING: 'Debug: initializing...',
  DEBUG_LOADING: (loading) => `Debug: loading=${loading.toString()}`,
  
  // Main Titles
  APP_TITLE_KIDS: '✨ Emmy\'s Learning Adventure ✨',
  APP_TITLE_PARENT: '✨ Emmy\'s First Grade Learning Adventure ✨',
  WELCOME_KIDS: (name) => `Hi ${name}! Ready to learn?`,
  WELCOME_PARENT: (name) => `Welcome, ${name}!`,
  
  // Progress Labels
  POINTS: 'Points',
  SUBJECTS: 'Subjects',
  DAY_STREAK: 'Day Streak',
  TOTAL_POINTS: 'Total Points',
  ACHIEVEMENTS: 'Achievements',
  
  // Streak Messages
  STREAK_GREAT_START: 'Great start!',
  STREAK_KEEP_IT_UP: 'Keep it up!',
  STREAK_AMAZING: 'Amazing!',
  STREAK_INCREDIBLE: 'Incredible!',
  
  // Achievement Messages
  ACHIEVEMENT_UNLOCKED: 'Achievement Unlocked!',
  ACHIEVEMENT_AWESOME: 'Awesome! 🎉',
  ACHIEVEMENT_POINTS: (points) => `+${points} Points!`,
  ACHIEVEMENT_POINTS_LABEL: (points) => `+${points} points`,
  
  // Daily Challenge
  DAILY_CHALLENGE_TITLE: 'Today\'s Challenge!',
  DAILY_CHALLENGE_REWARD: (points) => `Reward: ${points} points`,
  
  // Section Headers
  LEARNING_GAMES: '🎮 Learning Games',
  STUDY_GUIDES: '📚 Study Guides',
  QUICK_ACTIONS: '⚡ Quick Actions',
  PARENT_TOOLS: '📚 Parent Tools',
  
  // Button Labels
  RANDOM_GAME: '🎲 Random Game',
  CONTINUE_LAST: '🔄 Continue Last',
  MY_AWARDS: '🏅 My Awards',
  CUSTOMIZE: '🎨 Customize',
  MUSIC_OFF: '🔇 Music Off',
  MUSIC_ON: '🎵 Music On',
  SEARCH: '🔍 Search',
  KIDS_MODE: 'Kids Mode (Default)',
  PARENTS_MODE: 'Parents',
  KIDS: '👶 Kids',
  PARENTS: '👨‍👩‍👧‍👦 Parents',
  RESET_TO_KIDS: '🔄 Reset to Kids',
  
  // Button Titles (Tooltips)
  PROFILE_SETTINGS: 'Profile Settings',
  LOGOUT: 'Logout',
  KIDS_MODE_TITLE: 'Kids Mode (Default)',
  SWITCH_TO_PARENT_MODE: 'Switch to Parent Mode',
  SWITCH_TO_KIDS_MODE: 'Switch to Kids Mode (Default)',
  SEARCH_TITLE: 'Search for games and topics',
  RESET_TO_KIDS_TITLE: 'Reset to Kids Mode (Default)',
  
  // Breadcrumb Labels
  BREADCRUMB_HOME: 'Home',
  BREADCRUMB_PARENT_REFERENCE: 'Parent Reference',
  BREADCRUMB_ACHIEVEMENTS: 'Achievements',
  BREADCRUMB_CUSTOMIZE: 'Customize',
  BREADCRUMB_PROGRESS: 'Progress',
  BREADCRUMB_FEEDBACK: 'Feedback',
  BREADCRUMB_FIELD_TRIPS: 'Field Trips',
  BREADCRUMB_STUDY_GUIDES: 'Study Guides',
  
  // Search Interface
  SEARCH_PLACEHOLDER: 'Search words, subjects, or topics...',
  SEARCH_NO_RESULTS: (query) => `No results found for "${query}"`,
  KEYBOARD_SHORTCUT: 'Press S to search',
  
  // Offline Mode
  OFFLINE_MODE: 'Offline Mode',
  
  // Parent Tools Labels
  PARENT_TOOL_WORD_LISTS: 'Word Lists',
  PARENT_TOOL_QUICK_REFERENCE: 'Quick reference',
  PARENT_TOOL_PROGRESS: 'Progress',
  PARENT_TOOL_TRACK_PROGRESS: 'Track progress',
  PARENT_TOOL_AWARDS: 'Awards',
  PARENT_TOOL_VIEW_REWARDS: 'View rewards',
  PARENT_TOOL_FEEDBACK: 'Feedback',
  PARENT_TOOL_SHARE_THOUGHTS: 'Share thoughts',
  PARENT_TOOL_NEWSLETTER: 'Newsletter',
  PARENT_TOOL_WEEK_10: 'Week 10',
  PARENT_TOOL_CALENDAR: 'Calendar',
  PARENT_TOOL_SCHOOL_EVENTS: 'School events',
  PARENT_TOOL_FIELD_TRIPS: 'Field Trips',
  PARENT_TOOL_UPCOMING_TRIPS: 'Upcoming trips',
  PARENT_TOOL_SMORE: 'Smore',
  PARENT_TOOL_SCOPES: 'Scopes',
  
  // Subject Game Grid Labels
  SUBJECT_QUESTIONS: (count) => `${count} questions`,
  SUBJECT_SCORE: (score) => `Score: ${score}`,
  
  // Email Sharing Error Messages
  EMAIL_SEND_FAILED: 'Failed to send email. Please try using one of the email service options instead.',
  EMAIL_SHARE_FAILED: 'Failed to share progress. Please try again.',
  EMAIL_MODULE_FAILED: 'Failed to share module completion. Please try again.',
  
  // Share Message Defaults
  SHARE_MODULE_DEFAULT: (subjectName, percentage) => `I just completed ${subjectName} with a score of ${percentage}%! 🎉`
};

// Question Limits (for question selector modal)
export const QUESTION_LIMITS = {
  MIN: 1,
  MAX: 100,
  DEFAULT: 10
};

